import { ExtractionStatus } from "generated-graphql/graphql";

export enum DocumentReviewStatus {
  Analyzing = "ANALYZING",
  Reviewed = "REVIEWED",
  NeedsReview = "NEEDS_REVIEW",
  Failed = "FAILED",
  ManualEntry = "MANUAL_ENTRY",
  NotAttempted = "NOT_ATTEMPTED",
}

export const DOCUMENT_REVIEW_STATUS_LABELS: Record<
  DocumentReviewStatus,
  string
> = {
  [DocumentReviewStatus.Analyzing]: "🍭 Analyzing",
  [DocumentReviewStatus.Reviewed]: "🌿 Reviewed",
  [DocumentReviewStatus.NeedsReview]: "🔸 Needs Review",
  [DocumentReviewStatus.Failed]: "⚠️ Failed",
  [DocumentReviewStatus.ManualEntry]: "📝 Manual Entry",
  [DocumentReviewStatus.NotAttempted]: "👉 Not Attempted",
};

export const DOCUMENT_REVIEW_STATUS_ICONS: Record<
  DocumentReviewStatus,
  string
> = {
  [DocumentReviewStatus.Analyzing]: "🍭",
  [DocumentReviewStatus.Reviewed]: "🌿",
  [DocumentReviewStatus.NeedsReview]: "🔸",
  [DocumentReviewStatus.Failed]: "⚠️",
  [DocumentReviewStatus.ManualEntry]: "📝",
  [DocumentReviewStatus.NotAttempted]: "👉",
};

export const FILTERABLE_DOCUMENT_REVIEW_STATUSES: DocumentReviewStatus[] =
  Object.values(DocumentReviewStatus);

type DocumentReviewStatusRowLike = {
  extractionStatus?: ExtractionStatus | null;
  documentMetadata?: {
    needsReview?: boolean | null;
  } | null;
};

export const getDocumentReviewStatus = (
  row: DocumentReviewStatusRowLike
): DocumentReviewStatus => {
  switch (row.extractionStatus) {
    case ExtractionStatus.Pending:
      return DocumentReviewStatus.Analyzing;
    case ExtractionStatus.Succeeded:
      return row.documentMetadata?.needsReview === false
        ? DocumentReviewStatus.Reviewed
        : DocumentReviewStatus.NeedsReview;
    case ExtractionStatus.Failed:
      return DocumentReviewStatus.Failed;
    case ExtractionStatus.NotAttempted:
      // If the document metadata is not present, we need to check if the document was manually entered
      if (!row.documentMetadata) {
        // No metadata and status is "NOT_ATTEMPTED", so we return "NOT_ATTEMPTED"
        return DocumentReviewStatus.NotAttempted;
      }
      // If the document metadata is present, we need to check if the document was manually entered
      return row.documentMetadata.needsReview === false
        ? DocumentReviewStatus.ManualEntry
        : DocumentReviewStatus.NeedsReview;
    default:
      return DocumentReviewStatus.NotAttempted;
  }
};

export const getDocumentReviewStatusLabel = (
  status: DocumentReviewStatus
): string => DOCUMENT_REVIEW_STATUS_LABELS[status];

export const getDocumentReviewStatusIcon = (
  status: DocumentReviewStatus
): string => DOCUMENT_REVIEW_STATUS_ICONS[status];

export const getDocumentReviewStatusFromRow = (
  row: DocumentReviewStatusRowLike
): DocumentReviewStatus => getDocumentReviewStatus(row);

export const getDocumentReviewStatusLabelFromRow = (
  row: DocumentReviewStatusRowLike
): string => getDocumentReviewStatusLabel(getDocumentReviewStatus(row));

export const getDocumentReviewStatusIconFromRow = (
  row: DocumentReviewStatusRowLike
): string => getDocumentReviewStatusIcon(getDocumentReviewStatus(row));
